/**
 * Общие части экранов техники: плашки, вкладки, подписи полей, переключатель,
 * флажок. Ими собраны справочник техники, группы механизаторов и ставки групп
 * — поэтому они здесь, а не внутри одного виджета.
 */
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { Input, TextArea } from '@shared/ui/Input';
import { fleetLeading, fleetType } from './tokens';

export type NoticeTone = 'info' | 'warning' | 'danger' | 'success';

function toneColor(theme: DefaultTheme, tone: NoticeTone): string {
  return theme.colors.status[tone];
}

/**
 * Плашка с сообщением. Текст — основного цвета (оранжевый и зелёный темы
 * мелким текстом AA не проходят), тон — кромкой и подложкой.
 */
export const Notice = styled.div<{ $tone: NoticeTone }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme, $tone }) => toneColor(theme, $tone)}59;
  border-left: 3px solid ${({ theme, $tone }) => toneColor(theme, $tone)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $tone }) => toneColor(theme, $tone)}14;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  & > .text {
    flex: 1 1 240px;
    min-width: 0;
    text-wrap: pretty;
  }

  & strong {
    font-weight: 600;
  }
`;

/** Кнопка-ссылка внутри текста и плашек. */
export const TextButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-underline-offset: 3px;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.text.muted};
    cursor: default;
  }
`;

/** Подпись поля формы — обычным регистром: подписи паспорта длинные. */
export const FieldLabel = styled.label`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > .req {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

export const FieldHint = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const FieldError = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Поле ввода в состоянии ошибки: красная кромка поверх общего Input. */
export const invalidControl = css`
  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
`;

/** Сетка формы в боковой панели: две колонки, в узкой панели — одна. */
export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};

  @container drawer (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FormRow = styled.div<{ $wide?: boolean }>`
  min-width: 0;
  grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
`;

export const Control = styled(Input)`
  ${invalidControl}
`;

export const ControlArea = styled(TextArea)`
  min-height: 64px;
  ${invalidControl}
`;

const TabList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(5)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  flex: none;
  padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};
  border: none;
  background: none;
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: ${fleetType.base};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > .count {
    margin-left: ${({ theme }) => theme.spacing(1.5)};
    font-size: ${fleetType.small};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }
`;

export interface TabItem<T extends string> {
  id: T;
  label: string;
  count?: number;
}

export function tabId(prefix: string, id: string): string {
  return `${prefix}-tab-${id}`;
}

export function tabPanelId(prefix: string, id: string): string {
  return `${prefix}-panel-${id}`;
}

/**
 * Вкладки по образцу WAI-ARIA: стрелки, Home и End переключают и переносят
 * фокус, в Tab-обходе только активная вкладка.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  idPrefix,
  label,
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  idPrefix: string;
  label: string;
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>());

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = items.length - 1;
    let next = -1;
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    const target = items[next];
    if (!target) return;
    e.preventDefault();
    onChange(target.id);
    refs.current.get(target.id)?.focus();
  }

  return (
    <TabList role="tablist" aria-label={label}>
      {items.map((t, i) => {
        const active = t.id === value;
        return (
          <Tab
            key={t.id}
            ref={(el) => {
              if (el) refs.current.set(t.id, el);
              else refs.current.delete(t.id);
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, t.id)}
            aria-selected={active}
            aria-controls={tabPanelId(idPrefix, t.id)}
            tabIndex={active ? 0 : -1}
            $active={active}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {t.label}
            {t.count !== undefined && <span className="count">{t.count}</span>}
          </Tab>
        );
      })}
    </TabList>
  );
}

export function TabPanel({
  idPrefix,
  id,
  children,
}: {
  idPrefix: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <div role="tabpanel" id={tabPanelId(idPrefix, id)} aria-labelledby={tabId(idPrefix, id)}>
      {children}
    </div>
  );
}

/**
 * Кнопки во всю ширину узкого экрана с переносом подписи: длинная подпись
 * («Подобрать по должностям механизаторов») шире телефона в одну строку.
 */
export const stretchButtons = css`
  & > button,
  & > a {
    flex: 1 1 100%;
    height: auto;
    min-height: ${({ theme }) => theme.spacing(10)};
    padding-top: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(2)};
    white-space: normal;
    text-align: center;
  }
`;

/** Ряд кнопок с переносом — на узком экране кнопки уходят на новую строку. */
export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const Spacer = styled.span`
  flex: 1 1 auto;
`;

/** Статус в подвале панели: «Изменено полей: 3», «Сохранено». */
export const FooterStatus = styled.p`
  flex: 1 1 200px;
  min-width: 0;
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

/** Переключатель из нескольких кнопок (`aria-pressed`): вид списка, база ставки. */
export const Segmented = styled.div`
  display: inline-flex;
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

export const SegmentButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: ${fleetType.small};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.backgroundElevated};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
    box-shadow: ${({ theme }) => theme.shadow.xs};
  }
`;

/** Группа вариантов с подписью-легендой: единица учёта, база ставки, основание. */
export const ChoiceFieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  & > legend {
    margin-bottom: ${({ theme }) => theme.spacing(1.5)};
    padding: 0;
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};

  @container drawer (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/**
 * Вариант-карточка: радиокнопка, название `.title` и пояснение `.hint`;
 * выбранный — кромкой и подложкой, фокус — кольцом вокруг всей карточки.
 */
export const ChoiceCard = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:has(input:checked) {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  &:has(input:focus-visible) {
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  /* Недоступный вариант — контур без заливки и пояснение, почему. */
  &:has(input:disabled) {
    cursor: not-allowed;
    border-style: dashed;
    background: transparent;
  }

  &:has(input:disabled) > .title {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > input {
    width: 16px;
    height: 16px;
    margin: 2px 0 0;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  & > .title {
    font-size: ${fleetType.base};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > .hint {
    grid-column: 2;
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }
`;

/**
 * Ключевые цифры карточки — как шапка технического паспорта: сетка ячеек
 * «подпись — значение» через 1 px. `.span2` — ячейка на две колонки, `dd.big`
 * — крупная цифра, `dd.empty` — прочерк.
 */
export const SpecGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;

  & > div {
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  & > .span2 {
    grid-column: span 2;
  }

  & dt {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.primary};
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  & dd.big {
    font-size: ${fleetType.title};
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  & dd.empty {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  @container drawer (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

/**
 * Ссылка в соседний раздел, оформленная кнопкой («Ставки групп»,
 * «Группы механизаторов»): переход — это ссылка, а не действие.
 */
export const ButtonLink = styled(Link)<{ $variant: 'secondary' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: ${({ theme }) => theme.spacing(10)};
  padding: ${({ theme }) => `0 ${theme.spacing(4)}`};
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'secondary' ? theme.colors.border : 'transparent')};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $variant }) =>
    $variant === 'secondary' ? theme.colors.backgroundElevated : 'transparent'};
  color: ${({ theme, $variant }) =>
    $variant === 'secondary' ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${fleetType.base};
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === 'secondary' ? theme.colors.surfaceHover : theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > svg {
    flex: none;
  }
`;

/** Флажок с подписью в строке фильтров: «Показать архив». */
export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 40px;
  padding: ${({ theme }) => `0 ${theme.spacing(1)}`};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  cursor: pointer;

  & > input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }
`;
